/**
 * Programme-outline clip for live display-only layers — docs/DATA_SOURCES.md §1a rule 4.
 *
 * §1a, on WMS/WMTS that cannot be pre-clipped:
 *
 * > the caching proxy restricts requests to tiles intersecting `bbox`, and the frontend
 * > applies the existing AOI-clip mechanism (CSS clip-path pane, already built in the
 * > mockup) using the programme outline so nothing renders outside the four countries.
 *
 * The proxy half is `apps.datasets.wms_bounds`. This is the visual half, and it is needed
 * because the two cut at different resolutions: the proxy allows any *tile* that intersects
 * the programme bbox, and a z6 mercator tile that touches Burkina also covers most of Ghana
 * and a corner of the Atlantic.
 *
 * ## Two deliberate differences from `AoiClip`
 *
 * **1. The clip goes on the tile layer's own container, not on the pane.** §G6 gives
 * `rasterPane` and `choroPane` to the AOI clip and to `Swipe`, which are mutually exclusive
 * *with each other* and own `pane.style.clipPath` between them. A third writer on the same
 * property would silently win or lose depending on effect order. Leaflet gives every
 * `TileLayer` its own `.leaflet-layer` container div inside the pane, so writing the mask
 * there composes with the pane's clip instead of competing for it — an AOI clip and a
 * programme clip both applied is the intersection of the two, which is the right answer.
 *
 * **2. The geometry is in layer points, not container points.** §G6's reference
 * implementation uses `latLngToContainerPoint` and re-projects on `move`. A pane is a child
 * of Leaflet's `_mapPane`, whose CSS transform is what a pan actually moves, so the
 * coordinate system a `clip-path` on a pane child is resolved in is the **layer** point
 * space — origin at `map.getPixelOrigin()`, exactly where Leaflet positions markers and
 * tiles. Using layer points means the mask is glued to the ground by construction rather
 * than by how often it is recomputed, and it survives a fast pan (during which Leaflet
 * offsets `_mapPane` and fires `move` on a rAF, not per frame).
 *
 * **`path()`, not `polygon()`.** The programme is four countries — four disjoint rings —
 * and CSS `polygon()` takes exactly one. `path()` takes SVG path data, so the rings are
 * `M … Z M … Z`, and the default `nonzero` fill rule unions them.
 */
import { useEffect } from 'react';
import type L from 'leaflet';

/** One outer ring of the programme outline, as `[lon, lat]` pairs (the API's shape). */
export type LonLatRing = [number, number][];

/**
 * The `clip-path` value that masks `rings` to the map's current view, in **layer points**.
 *
 * Returns `''` when there is nothing to mask with, which is the same string that releases a
 * clip — so "no outline loaded yet" and "clip switched off" take the same code path and a
 * missing payload can never leave a stale mask behind.
 *
 * Named for what it does rather than for the programme outline it was written for: client
 * round 4's E4 mask cuts the raster pane to a §F5.8 scope unit with the same three lines
 * (`features/map/ScopeClip.ts`), and two copies of a projection loop is two places for a
 * rounding rule to drift.
 */
export function ringsClipPath(map: L.Map, rings: LonLatRing[] | undefined): string {
  if (!rings?.length) return '';
  const parts: string[] = [];
  for (const ring of rings) {
    if (ring.length < 3) continue;
    const points = ring.map(([lon, lat]) => {
      const p = map.latLngToLayerPoint([lat, lon]);
      // Rounded to whole pixels: the string is rebuilt on every zoom and re-parsed by the
      // style system, and sub-pixel precision triples its length for a mask edge no display
      // can show.
      return `${Math.round(p.x)} ${Math.round(p.y)}`;
    });
    parts.push(`M ${points.join(' L ')} Z`);
  }
  if (!parts.length) return '';
  return `path('${parts.join(' ')}')`;
}

/** Write (or release, with `''`) a clip on one Leaflet layer's container. */
export function setLayerClip(layer: L.TileLayer, value: string): void {
  const container = layer.getContainer();
  if (container) container.style.clipPath = value;
}

/**
 * Keep `layer` masked to the programme outline while `enabled`, re-projecting on view change.
 *
 * Subscribes to the same four events `useAoiClip` does. `zoom` and `viewreset` are the ones
 * that matter — layer points are stable under a pan — but `move` and `resize` cost a string
 * rebuild each and remove a whole class of "which events change the pixel origin" reasoning
 * from a mask that must never be wrong.
 */
export function useProgrammeClip(
  map: L.Map,
  layer: L.TileLayer | null,
  rings: LonLatRing[] | undefined,
  enabled: boolean,
): void {
  useEffect(() => {
    if (!layer) return;
    if (!enabled || !rings?.length) {
      setLayerClip(layer, '');
      return;
    }
    const apply = () => setLayerClip(layer, ringsClipPath(map, rings));
    apply();
    map.on('move zoom viewreset resize', apply);
    return () => {
      map.off('move zoom viewreset resize', apply);
      setLayerClip(layer, '');
    };
  }, [map, layer, rings, enabled]);
}
